/**
 * Service Worker Manager for Mobile Web Optimization
 * Handles service worker registration, updates, and communication
 */

export interface ServiceWorkerConfig {
  enableServiceWorker: boolean;
  enableBackgroundSync: boolean;
  enablePushNotifications: boolean;
  cacheStrategy: 'aggressive' | 'conservative' | 'minimal';
  updateCheckInterval: number; // in milliseconds
}

export interface CacheStats {
  [key: string]: {
    name: string;
    entries: number;
    urls: string[];
    error?: string;
  };
}

export interface OfflineAction {
  id: string;
  type: string;
  payload: any;
  url: string;
  method?: string;
  headers?: Record<string, string>;
  timestamp: number;
  retryCount?: number;
}

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private config: ServiceWorkerConfig;
  private updateCheckTimer: number | null = null;
  private messageChannel: MessageChannel | null = null;

  constructor(config: Partial<ServiceWorkerConfig> = {}) {
    this.config = {
      enableServiceWorker: true,
      enableBackgroundSync: true,
      enablePushNotifications: false,
      cacheStrategy: 'conservative',
      updateCheckInterval: 60000, // 1 minute
      ...config,
    };
  }

  /**
   * Initialize service worker
   */
  public async initialize(): Promise<void> {
    if (!this.config.enableServiceWorker || !('serviceWorker' in navigator)) {
      console.log('Service Worker not supported or disabled');
      return;
    }

    try {
      console.log('🔄 Registering Service Worker...');
      
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none', // Always check for updates
      });

      console.log('✅ Service Worker registered successfully', this.registration);

      // Set up event listeners first
      this.setupEventListeners();

      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      console.log('🚀 Service Worker ready', registration);
      
      // Check if we have a controller (active service worker)
      if (navigator.serviceWorker.controller) {
        console.log('✅ Service Worker is controlling the page');
      } else {
        console.log('⏳ Service Worker not controlling yet, waiting...');
        
        // For first-time registration, we need to reload or wait for activation
        if (this.registration.active) {
          console.log('🔄 Service Worker is active, claiming clients...');
          // The SW should claim clients automatically, but let's wait a bit
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Check for updates periodically
      this.startUpdateCheck();

      // Handle initial installation
      if (this.registration.installing) {
        console.log('📦 Service Worker installing...');
        this.handleServiceWorkerInstalling(this.registration.installing);
      }

      // Handle waiting service worker
      if (this.registration.waiting) {
        console.log('⏸️ Service Worker waiting...');
        this.handleServiceWorkerWaiting(this.registration.waiting);
      }

      // Log final status
      console.log('📊 Service Worker Status:', {
        registration: !!this.registration,
        controller: !!navigator.serviceWorker.controller,
        active: !!this.registration.active,
        installing: !!this.registration.installing,
        waiting: !!this.registration.waiting
      });

    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
    }
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    if (!this.registration) return;

    // Listen for service worker updates
    this.registration.addEventListener('updatefound', () => {
      const newWorker = this.registration!.installing;
      if (newWorker) {
        this.handleServiceWorkerInstalling(newWorker);
      }
    });

    // Listen for controller changes
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service Worker controller changed');
      // Reload the page to use the new service worker
      window.location.reload();
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      this.handleServiceWorkerMessage(event);
    });
  }

  /**
   * Handle service worker installing
   */
  private handleServiceWorkerInstalling(worker: ServiceWorker): void {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') {
        if (navigator.serviceWorker.controller) {
          // New service worker available
          this.notifyUpdateAvailable();
        } else {
          // Service worker installed for the first time
          console.log('Service Worker installed for the first time');
        }
      }
    });
  }

  /**
   * Handle service worker waiting
   */
  private handleServiceWorkerWaiting(_worker: ServiceWorker): void {
    this.notifyUpdateAvailable();
  }

  /**
   * Notify user about available update
   */
  private notifyUpdateAvailable(): void {
    // Dispatch custom event for UI to handle
    const event = new CustomEvent('sw-update-available', {
      detail: {
        registration: this.registration,
        skipWaiting: () => this.skipWaiting(),
      },
    });
    window.dispatchEvent(event);
  }

  /**
   * Skip waiting and activate new service worker
   */
  public async skipWaiting(): Promise<void> {
    if (!this.registration?.waiting) return;

    // Send skip waiting message to service worker
    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }

  /**
   * Start periodic update checks
   */
  private startUpdateCheck(): void {
    if (this.updateCheckTimer) {
      clearInterval(this.updateCheckTimer);
    }

    this.updateCheckTimer = window.setInterval(() => {
      this.checkForUpdates();
    }, this.config.updateCheckInterval);
  }

  /**
   * Check for service worker updates
   */
  public async checkForUpdates(): Promise<void> {
    if (!this.registration) return;

    try {
      await this.registration.update();
    } catch (error) {
      console.error('Failed to check for service worker updates:', error);
    }
  }

  /**
   * Send message to service worker
   */
  private async sendMessage(message: any): Promise<any> {
    console.log('📤 Sending message to Service Worker:', message.type);
    
    // Check if service worker is available
    if (!navigator.serviceWorker.controller) {
      console.log('⚠️ No Service Worker controller, waiting for ready state...');
      try {
        await navigator.serviceWorker.ready;
        // If still no controller, the SW might not be activated yet
        if (!navigator.serviceWorker.controller) {
          console.log('❌ Service Worker still not controlling the page');
          throw new Error('Service Worker not controlling the page yet');
        }
        console.log('✅ Service Worker controller now available');
      } catch (error) {
        console.error('❌ Service Worker not available:', error);
        throw new Error('Service Worker not available');
      }
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        console.log('⏰ Service Worker message timeout for:', message.type);
        reject(new Error('Service Worker message timeout'));
      }, 5000);

      // Create a new MessageChannel for each message to avoid port neutering
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        console.log('📥 Received response from Service Worker:', event.data.type);
        clearTimeout(timeout);
        messageChannel.port1.close();
        messageChannel.port2.close();
        
        // Handle error responses from service worker
        if (event.data.type === 'ERROR') {
          console.error('❌ Service Worker error:', event.data.payload.error);
          reject(new Error(event.data.payload.error));
        } else {
          resolve(event.data);
        }
      };

      try {
        navigator.serviceWorker.controller!.postMessage(message, [messageChannel.port2]);
        console.log('✅ Message sent to Service Worker');
      } catch (error) {
        clearTimeout(timeout);
        messageChannel.port1.close();
        messageChannel.port2.close();
        console.error('❌ Failed to send message to Service Worker:', error);
        reject(error);
      }
    });
  }

  /**
   * Handle messages from service worker
   */
  private handleServiceWorkerMessage(event: MessageEvent): void {
    const { type, payload } = event.data;

    switch (type) {
      case 'CACHE_UPDATED':
        console.log('Cache updated:', payload);
        break;
      case 'OFFLINE_ACTION_QUEUED':
        console.log('Offline action queued:', payload);
        break;
      case 'BACKGROUND_SYNC_SUCCESS':
        console.log('Background sync completed:', payload);
        break;
      default:
        console.log('Unknown service worker message:', event.data);
    }
  }

  /**
   * Get cache statistics
   */
  public async getCacheStats(): Promise<CacheStats> {
    try {
      // Check if service worker is ready
      if (!this.isActive()) {
        console.log('Service Worker not active yet, returning empty stats');
        return {};
      }
      
      const response = await this.sendMessage({ type: 'GET_CACHE_STATS' });
      return response.payload;
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return {};
    }
  }

  /**
   * Clear specific cache
   */
  public async clearCache(cacheName: string): Promise<void> {
    try {
      await this.sendMessage({ 
        type: 'CLEAR_CACHE', 
        payload: { cacheName } 
      });
      console.log(`Cache cleared: ${cacheName}`);
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  /**
   * Preload resources
   */
  public async preloadResources(resources: Array<{ url: string; type: string }>): Promise<void> {
    try {
      await this.sendMessage({ 
        type: 'PRELOAD_RESOURCES', 
        payload: { resources } 
      });
      console.log('Resources preloaded:', resources.length);
    } catch (error) {
      console.error('Failed to preload resources:', error);
    }
  }

  /**
   * Queue offline action
   */
  public async queueOfflineAction(action: Omit<OfflineAction, 'id' | 'timestamp'>): Promise<void> {
    const offlineAction: OfflineAction = {
      ...action,
      id: this.generateActionId(),
      timestamp: Date.now(),
    };

    try {
      await this.storeOfflineAction(offlineAction);
      
      // Register background sync if supported
      if (this.config.enableBackgroundSync && 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        const registration = await navigator.serviceWorker.ready;
        await (registration as any).sync.register('background-sync');
      }
    } catch (error) {
      console.error('Failed to queue offline action:', error);
    }
  }

  /**
   * Store offline action in IndexedDB
   */
  private async storeOfflineAction(action: OfflineAction): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SnapalyzeOffline', 1);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['actions'], 'readwrite');
        const store = transaction.objectStore('actions');
        const addRequest = store.add(action);
        
        addRequest.onsuccess = () => resolve();
        addRequest.onerror = () => reject(addRequest.error);
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('actions')) {
          const store = db.createObjectStore('actions', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp');
          store.createIndex('retryCount', 'retryCount');
        }
      };
    });
  }

  /**
   * Generate unique action ID
   */
  private generateActionId(): string {
    return `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Request push notification permission
   */
  public async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Subscribe to push notifications
   */
  public async subscribeToPushNotifications(vapidPublicKey: string): Promise<PushSubscription | null> {
    if (!this.registration || !this.config.enablePushNotifications) {
      return null;
    }

    try {
      const permission = await this.requestNotificationPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
      });

      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<ServiceWorkerConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Restart update check with new interval
    if (newConfig.updateCheckInterval) {
      this.startUpdateCheck();
    }
  }

  /**
   * Get current configuration
   */
  public getConfig(): ServiceWorkerConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    if (this.updateCheckTimer) {
      clearInterval(this.updateCheckTimer);
      this.updateCheckTimer = null;
    }

    if (this.messageChannel) {
      this.messageChannel.port1.close();
      this.messageChannel.port2.close();
      this.messageChannel = null;
    }
  }

  /**
   * Check if service worker is supported and active
   */
  public isActive(): boolean {
    return !!(navigator.serviceWorker?.controller);
  }

  /**
   * Get service worker registration
   */
  public getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }
}

// Export singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

export default ServiceWorkerManager;
